<template>
  <div class="grid gap-y-10">
    <div class="grid gap-10 sm:gap-x-8" :class="{ 'sm:grid-cols-2': promotedPosts?.length === 2 }" v-if="promotedPosts?.length >= 2" v-auto-animate>
      <div v-for="post in promotedPosts" :key="post.id">
        <NuxtLink :to="post.path" class="block">
          <PostsPromotedCard :post="post" />
        </NuxtLink>
      </div>
    </div>
    <div class="grid grid-cols-12 gap-x-4 gap-y-10 md:gap-x-8" v-if="posts?.length" v-auto-animate>
      <div class="col-span-full sm:col-span-6 xl:col-span-4" v-for="post in posts" :key="post.id">
        <NuxtLink :to="post.path" class="block">
          <PostsCard :post="post" />
        </NuxtLink>
      </div>
    </div>
    <div class="mt-8 mb-8 text-sm flex items-center gap-4" v-if="allowPagination">
      <NuxtLink :to="{ query: { paged: allowPagination ? paged+1 : paged }}" class="button-coral-outline" :class="{ 'disabled': !allowPagination }">
        {{ $t('shared.loadMore') }}
      </NuxtLink>
    </div>
    <div class="mt-8 mb-8" v-if="app.pageviews < 2 && allowPagination">
      <div class="flex items-center gap-2">
        <NuxtLink :to="{ query: { paged: undefined }}" :class="{ 'opacity-20': paged === 1 }">
          <AtomIcon name="circle-arrow-left" />
        </NuxtLink>
        <div class="text-sm">  
          <NuxtLink :to="{ query: { paged: page }}" v-for="page in pagination" :key="page" class="px-2" :class="{ 'opacity-50': page !== paged }">
            {{ page }}
          </NuxtLink>
        </div>
        <NuxtLink :to="{ query: { paged: pages }}" :class="{ 'opacity-20': !allowPagination }">
          <AtomIcon name="circle-arrow-right" />
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script async setup>
const route = useRoute()
const app = useAppStore()
const limit = ref(12)
const paged = computed(() => parseInt(route.query.paged || 1))
const pages = computed(() => Math.ceil(data.value?.total / limit.value))
const offset = computed(() => (parseInt(paged.value) - 1) * limit.value)
const allowPagination = computed(() => data?.value?.posts?.length === limit.value)

// Get category slug
const categorySlug = computed(() => route.path?.split('/')[1] || undefined)

// Get city slug
const citySlug = computed(() => route.path?.split('/')[2] || undefined)

// Calculate visible pages
const pagination = computed(() => {
  const range = 2 // Show ±2 pages around the current page
  let start = Math.max(1, paged.value - range)
  let end = Math.min(pages.value, paged.value + range)

  // Ensure we always show 5 pages when possible
  if (end - start + 1 < 5) {
    if (start === 1) {
      end = Math.min(pages.value, start + 4)
    } else if (end === pages.value) {
      start = Math.max(1, end - 4)
    }
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
})

// Fetch start (for promoted posts)
const { data: startData } = await useFetch(`/start`, { 
  key: `start-${citySlug.value}`,
  baseURL: useEnv('cmsBase'),
  query: {
    citySlug: computed(() => citySlug.value),
  }
})

// Grep promoted post (filter on category and slice to 2 posts)
const promotedPosts = computed(() => startData.value?.posts?.promoted
  ?.filter(post => post.categories.map(cat => cat.slug).includes(categorySlug.value))
  ?.slice(0, 2))

// Grep promoted post ID's
const promotedPostIds = computed(() => promotedPosts?.value?.map(post => post.id))

// Fetch posts
const { data, status } = await useFetch('/posts', {
  key: `posts-${categorySlug.value}-${citySlug.value}`,
  baseURL: useEnv('cmsBase'),
  query: {
    limit: limit.value,
    offset: computed(() => offset.value),
    category: computed(() => categorySlug.value),
    tag: computed(() => citySlug.value),
    orderby: categorySlug.value === 'guider'
      ? 'modified'
      : 'date',
    excludedIds: !offset.value && promotedPostIds.value?.length >= 2
      ? promotedPostIds.value?.join(',') 
      : undefined,
  }
})

// Append posts on initial load
const posts = ref(data.value?.posts)

// Prepend promoted posts if only ONE post
if (promotedPostIds.value?.length === 1) {
  posts.value = useUniqueBy([...promotedPosts.value, ...posts.value], 'id')
}

// Handle pagination as load more
watch(() => status.value, () => {
  if (route.query?.paged) {
    posts.value = useUniqueBy([...posts.value, ...data.value?.posts], 'id')
  } else {
    window.scrollTo(0, 0)
    posts.value = data.value?.posts
  }
})
</script>